import { ReactComponent as ExpandIcon } from 'assets/images/icons/small-button/expand.svg';
import ErrorFallback from 'components/error-fallback/ErrorFallback';
import useIsReadOnlyMode from 'components/v2/compiler/hooks/useIsReadOnlyMode';
import MaximizedDialog from 'components/v2/compiler/panels/expandable/MaximizedDialog';
import { Column, ColumnEvent, ColumnProps } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import React, { useId, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useUpdateLcaCustomResultGridDataRow } from 'services/api/mutationsLcaResults';
import { HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';
import { LcaCustomResultGridDataModel } from 'types/types';

import { DataTableStyled, GridButtonGreen } from '../../StyledGrid';
import { LcaGridNote } from '../../GridService';
import Tooltip from 'components/controls/Tooltip';
import { useLcaImportVersion } from '../util/LcaImportContext';

type TProps = {
  epdVersionId: string;
  customResultId: string;
  title?: React.ReactNode;
  rows?: LcaCustomResultGridDataModel[] | undefined;
  columns: ColumnProps[];
  notes: LcaGridNote[];
  disabled?: boolean;
  preferEnabledAppearanceEvenWhenDisabled?: boolean;
  className?: string;
};
const LcaCustomFixedResultsGrid = ({
  epdVersionId,
  customResultId,
  title,
  rows,
  columns,
  notes,
  disabled,
  preferEnabledAppearanceEvenWhenDisabled,
  className,
}: TProps) => {
  const updateMutation = useUpdateLcaCustomResultGridDataRow(epdVersionId, customResultId);
  const [visible, setVisible] = useState(false);
  const isReadOnly = useIsReadOnlyMode() || disabled;
  const expandButtonId = useId();
  const importVersion = useLcaImportVersion();

  const rowClassName = () => ({ 'p-disabled': isReadOnly && !preferEnabledAppearanceEvenWhenDisabled });

  const header = (
    <div className="flex flex-wrap align-items-center justify-content-between gap-2">
      <HeaderCaptionSemiBold className="white-space-nowrap">{title}</HeaderCaptionSemiBold>
      <span style={{ display: 'flex', columnGap: '5px' }}>
        {!visible && (
          <>
            <GridButtonGreen id={expandButtonId} icon={<ExpandIcon />} onClick={() => setVisible(true)} />
            <Tooltip targetId={expandButtonId} position="title">
              Expand
            </Tooltip>
          </>
        )}
      </span>
    </div>
  );

  const footerGroup = (
    <ColumnGroup>
      {notes.map((x, i) => (
        <Row key={i}>
          <Column footer={x.note} className="p-row-note" />
          <Column footer={x.description} colSpan={columns.length} className="p-row-note" />
        </Row>
      ))}
    </ColumnGroup>
  );

  const onCellEditComplete = (e: ColumnEvent) => {
    let { rowData, newValue, field } = e;
    const newData = {
      ...rowData,
      [field]: newValue,
    };
    updateMutation.mutate(newData);
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <MaximizedDialog title="" visible={visible} onHide={() => setVisible(false)}>
        <DataTableStyled
          key={`${customResultId}-${importVersion}`}
          value={rows}
          dataKey="ord"
          tableStyle={{ minWidth: '49rem' }}
          showGridlines
          footerColumnGroup={footerGroup}
          editMode="cell"
          header={header}
          rowClassName={rowClassName}
        >
          {columns.map((x) => (
            <Column
              key={x.field}
              {...x}
              onCellEditComplete={x.editor ? onCellEditComplete : undefined}
              {...(isReadOnly && { editor: undefined })}
            />
          ))}
        </DataTableStyled>
      </MaximizedDialog>
      <DataTableStyled
        key={`main-${customResultId}-${importVersion}`}
        className={className}
        value={rows}
        dataKey="ord"
        tableStyle={{ minWidth: '49rem' }}
        style={{ overflow: 'auto', display: 'grid' }}
        showGridlines
        footerColumnGroup={footerGroup}
        editMode="cell"
        header={header}
        rowClassName={rowClassName}
      >
        {columns.map((x) => (
          <Column
            key={x.field}
            {...x}
            onCellEditComplete={x.editor ? onCellEditComplete : undefined}
            {...(isReadOnly && { editor: undefined })}
          />
        ))}
      </DataTableStyled>
    </ErrorBoundary>
  );
};

export default LcaCustomFixedResultsGrid;
